import {
  BooleanField,
  DateField,
  DateTimeField,
  findValueForPropertyInModelContext,
  IModelContext,
  IValidationResult,
  ModelGroup,
  ModelItem,
  ModelOption,
  NumberField,
  RequiredFunction,
  StringField,
  UnmanagedField,
  ValidationFunction,
  ValueOptions
} from '@mng/model';
import {
  createFzKontoBezeichnungField,
  createFzKontoDienststelleRequiredField,
  createFzKontoExklusivAbberNrField,
  createFzKontoGiltBisField,
  createFzKontoGiltVonField,
  createFzKontoKontoField,
  createFzKontoKontoNameField,
  createFzKontoPersonalExplanationField,
  createFzKontoPersonalNummerRegexField,
  createFzKontoVerrechnungsartField,
  createFzPersNrErforderlichField
} from '../allgemein/fz-konto-base';
import { createVermittlerInfoBlattAbberGroup, createVermittlerInformationGroup } from '../allgemein/vermittler-model';
import { createSvPersonenDataModelGroup } from './svperson.model';
import { StammdatenAdresseBase } from '../allgemein/stammdaten-base';
import { ITEM_RISIKO_ADRESSE_NOTWENDIG } from '../svangebot.model.paths';

export const OPTION_LAENDER_OESTERREICH: ModelOption = { value: 'AT', name: 'Österreich' };

export const ADDRESS_VALID = 'VALID';
export const ADDRESS_INVALID = 'INVALID';
export const ADDRESS_VALID_STATE_UNKNOWN = '';

export function createSvAngebotModel(): ModelGroup {
  const vermittlerGroup: ModelGroup = createVermittlerInformationGroup().withPathPrefix('SV');
  const vermittlerInfoBlattAbberGroup: ModelGroup = createVermittlerInfoBlattAbberGroup()
    .withPathPrefix('SV')
    // Workaround zum Verhindern eines zweiten VermittlerDto Imports
    .withImportDef({ localImport: true });

  const model: ModelGroup = new ModelGroup('svAngebotData').addItems(
    new StringField('id').withReadOnly(),
    new DateTimeField('modifiedDate').withReadOnly(),
    new BooleanField('vertragsAenderung').withReadOnly(),
    new DateField('vertragszustandPerDatum', 'Datum an dem der Vertragszustand ausgelesen wurde').withReadOnly(),
    new BooleanField('vertragsAenderungInitialisiert').withReadOnly(),
    new BooleanField('iddBeratungDurchgefuehrt').withReadOnly(),
    new NumberField('vertragsNummer').withReadOnly(),
    new NumberField('versionsNummer').withReadOnly(),
    new DateField('vertragsAblaufDatum', 'Datum an dem der Vertrag abläuft').withReadOnly(),
    new UnmanagedField('produkt', 'Produkt'),

    createSvAngebotFzKontoModelGroup(),
    new StringField('bestandFzKontoNr').withReadOnly(),
    vermittlerGroup,
    vermittlerInfoBlattAbberGroup,
    new BooleanField('risikoAdresseNotwendig').withReadOnly(),
    createRisikoAdresseModelGroup(),
    createBeratungsdaten()
  );

  model.addGroupObject(createSvPersonenDataModelGroup());
  model.addArrayGroupObject(createSvRisikoModelGroup());

  return model;
}

function createBeratungsdaten(): ModelGroup {
  return new ModelGroup('beratungsdaten')
    .withInterfaceName('SvBeratungsdaten')
    .addItems(new StringField('produktKategorie').withReadOnly())
    .addItems(new StringField('produktNummer').withReadOnly())
    .addArrayGroupObject(createBeratungsdatenZweige())
    .withRemoveDataWhenNotRelevant(true);
}

function createBeratungsdatenZweige(): ModelGroup {
  return new ModelGroup('zweige')
    .withInterfaceName('SvBeratungsdatenZweig')
    .addItems(new StringField('id').withReadOnly())
    .addArrayGroupObject(createBeratungsdatenLeistungsarten())
    .withRemoveDataWhenNotRelevant(true);
}

function createBeratungsdatenLeistungsarten(): ModelGroup {
  return new ModelGroup('leistungsarten')
    .withInterfaceName('SvBeratungsdatenLeistungsart')
    .addItems(new StringField('id').withReadOnly());
}

export function createSvRisikoModelGroup(): ModelGroup {
  const bestandsRisikenModelGroup: ModelGroup = new ModelGroup('bestandsRisken', 'Bestandsrisiken')
    .withInterfaceName('SvRisiko')
    .addItems(
      new NumberField('vs').withReadOnly(),
      new NumberField('praemie').withReadOnly(),
      new BooleanField('autoVerlaengerung').withReadOnly(),
      new StringField('zahlweise').withReadOnly()
    );

  bestandsRisikenModelGroup.addArrayGroupObject(createSvZweigModelGroup());
  return bestandsRisikenModelGroup;
}

function createSvZweigModelGroup(): ModelGroup {
  const zweigeModelGroup: ModelGroup = new ModelGroup('zweige', 'SV Zweig')
    .withInterfaceName('SvZweig')
    .addItems(
      new StringField('key').withReadOnly(),
      new StringField('kbez').withReadOnly(),
      new StringField('beschreibung').withReadOnly(),
      new NumberField('praemie').withReadOnly(),
      new NumberField('jahresNettoPraemie').withReadOnly()
    );
  return zweigeModelGroup;
}

function createSvAngebotFzKontoModelGroup(): ModelGroup {
  const fzKonto: ModelGroup = new ModelGroup('fzKonto', 'FZ Konto')
    .withInterfaceName('SvAngebotFzKonto')
    .addItems(...createSvFzKontoFields());
  return fzKonto;
}

export function createSvFzKontoFields(): StringField[] {
  return [
    createFzKontoKontoField(),
    createFzKontoKontoNameField(),
    createFzKontoExklusivAbberNrField().withRemoveDataWhenNotRelevant().asArray(),
    createFzKontoVerrechnungsartField(),
    createFzKontoBezeichnungField(),
    createFzKontoGiltVonField(),
    createFzKontoGiltBisField(),
    createFzKontoPersonalNummerRegexField(),
    createFzKontoPersonalExplanationField(),
    createFzKontoDienststelleRequiredField(),
    createFzPersNrErforderlichField()
  ];
}

export class SvRisikoAdresseBase extends StammdatenAdresseBase {
  checkState: StringField;
  constructor(readOnly: boolean, id = 'risikoAdresse', interfaceName = 'SvRisikoAdresse') {
    super(readOnly, id, interfaceName);
    this.checkState = new StringField('checkState', 'CheckState').withReadOnly(readOnly);
    this.adresse.addItems(this.checkState);
  }
}

function createRisikoAdresseModelGroup(): ModelGroup {
  const risikoAdresse = new SvRisikoAdresseBase(false);

  risikoAdresse.strasse.withMaxlength(100).withRequired(true);
  risikoAdresse.hausnr.withMaxlength(15).withRequired(true);
  risikoAdresse.plz.withMaxlength(10).withRequired(true);
  risikoAdresse.ort.withMaxlength(100).withRequired(true);
  risikoAdresse.ikz.withRequired(true).withValueOptions(new ValueOptions().withOptions([OPTION_LAENDER_OESTERREICH]));

  const checkStateValidationFunction: ValidationFunction = (
    modelItem: ModelItem,
    modelContext: IModelContext
  ): IValidationResult | null => {
    // Bei einem alten Angebot ist der Wert von checkState undefined, in diesem Fall soll kein Fehler gemeldet werden.
    // Wenn der Wert undefined ist, liefert (modelContext.value === null) false
    return modelContext.value === null || modelContext.value === ADDRESS_VALID_STATE_UNKNOWN
      ? { id: 'addressNotChecked', message: 'Die Adresse ist nicht geprüft!' }
      : null;
  };

  risikoAdresse.checkState.withValidation(checkStateValidationFunction);

  const risikoAdresseRequired: RequiredFunction = (modelContext: IModelContext) => {
    const risikoAdresseNotwendig = findValueForPropertyInModelContext(
      modelContext,
      ITEM_RISIKO_ADRESSE_NOTWENDIG
    ) as boolean;
    return risikoAdresseNotwendig;
  };

  risikoAdresse.adresse.withRequiredFunction(risikoAdresseRequired);

  return risikoAdresse.adresse;
}
